/**
 * Light, dark, or whatever the machine says.
 *
 * Bootstrap 5.3 reads one attribute — `data-bs-theme` on <html> — so the
 * choice is resolved here, once, and written there. "auto" is the default and
 * keeps following `prefers-color-scheme` while the page is open, which is the
 * behaviour someone expects from a preference they never set. The choice is
 * remembered in localStorage; a browser that refuses it (private mode, storage
 * blocked) silently falls back to auto rather than throwing on first paint.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import Dropdown from "react-bootstrap/Dropdown";

export type ThemeChoice = "auto" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "emb-theme";
const CHOICES: readonly ThemeChoice[] = ["auto", "light", "dark"];

interface ThemeState {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readStoredChoice(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && (CHOICES as readonly string[]).includes(stored)) return stored as ThemeChoice;
  } catch {
    // Storage unavailable. Auto is the right answer anyway.
  }
  return "auto";
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);

  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (): void => setSystem(query.matches ? "dark" : "light");
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolved: ResolvedTheme = choice === "auto" ? system : choice;

  useEffect(() => {
    document.documentElement.setAttribute("data-bs-theme", resolved);
    // Keeps native widgets (scrollbars, date pickers, the autofill overlay) in
    // step with the palette Bootstrap just switched to.
    document.documentElement.style.colorScheme = resolved;
  }, [resolved]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not remembering it is a smaller failure than not applying it.
    }
  }, []);

  const value = useMemo<ThemeState>(() => ({ choice, resolved, setChoice }), [choice, resolved, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const theme = useContext(ThemeContext);
  if (!theme) throw new Error("useTheme was called outside <ThemeProvider>; wrap the app in it (see main.tsx).");
  return theme;
}

function SunIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="8" cy="8" r="3.1" />
      <path strokeLinecap="round" d="M8 1v1.6M8 13.4V15M15 8h-1.6M2.6 8H1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1M12.9 12.9l-1.1-1.1M4.2 4.2 3.1 3.1" />
    </svg>
  );
}

function MoonIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <path strokeLinejoin="round" d="M13.4 9.6A5.8 5.8 0 0 1 6.4 2.6a5.8 5.8 0 1 0 7 7Z" />
    </svg>
  );
}

function AutoIcon(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="8" cy="8" r="6.2" />
      <path fill="currentColor" stroke="none" d="M8 1.8a6.2 6.2 0 0 1 0 12.4Z" />
    </svg>
  );
}

const ICONS: Record<ThemeChoice, () => ReactNode> = { auto: AutoIcon, light: SunIcon, dark: MoonIcon };
const LABELS: Record<ThemeChoice, string> = { auto: "Match system", light: "Light", dark: "Dark" };

export function ThemeToggle(): ReactNode {
  const { choice, setChoice } = useTheme();
  const Icon = ICONS[choice];
  return (
    <Dropdown align="end">
      <Dropdown.Toggle variant="link" size="sm" className="text-body-secondary text-decoration-none px-2" id="emb-theme-toggle">
        <span className="visually-hidden">Colour theme: {LABELS[choice]}. Change</span>
        <Icon />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {CHOICES.map((option) => {
          const OptionIcon = ICONS[option];
          return (
            <Dropdown.Item key={option} active={option === choice} onClick={() => setChoice(option)} className="d-flex align-items-center gap-2">
              <OptionIcon />
              {LABELS[option]}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
